import { ResourcePlacement } from '../../../enums/enums';
import { FileObjectStatus, FileObjectType, type IFileObjectDTO } from '../../../models/fileObjects';
import { getDateOffset } from '../support/date-utils';

interface DevSurveyFixture {
  id: string;
  clientId: string;
  company: string;
  type: string;
  target: string;
  code: string;
}

// --- Content Fixtures ---

export const baseLearningTools: IFileObjectDTO[] = [
  {
    id: 'learning-tool-home-1',
    filename: 'executive-presence-guide.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 1200000,
    azureUrl: 'https://example.com/executive-presence-guide',
    updatedAt: new Date(getDateOffset(-2, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80',
    title: 'Executive Presence Guide',
    placement: ResourcePlacement.Homepage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  },
  {
    id: 'learning-tool-home-2',
    filename: 'coaching-reflection-canvas.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 980000,
    azureUrl: 'https://example.com/coaching-reflection-canvas',
    updatedAt: new Date(getDateOffset(-4, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=900&q=80',
    title: 'Coaching Reflection Canvas',
    placement: ResourcePlacement.Homepage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  },
  {
    // WHY: a third Homepage tool lets the dashboard carousel actually page and snap in dev preview (POL-10).
    id: 'learning-tool-home-3',
    filename: 'feedback-conversation-playbook.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 860000,
    azureUrl: 'https://example.com/feedback-conversation-playbook',
    updatedAt: new Date(getDateOffset(-3, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=900&q=80',
    title: 'Feedback Conversation Playbook',
    placement: ResourcePlacement.Homepage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  },
  {
    id: 'learning-tool-resource-1',
    filename: 'strategic-communication-toolkit.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 1500000,
    azureUrl: 'https://example.com/strategic-communication-toolkit',
    updatedAt: new Date(getDateOffset(-6, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=900&q=80',
    title: 'Strategic Communication Toolkit',
    placement: ResourcePlacement.ResourcesPage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  },
  {
    // WHY: second and third ResourcesPage tools let the Learning page ToolsCarousel page and snap (POL-10).
    id: 'learning-tool-resource-2',
    filename: 'delegation-decision-matrix.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 720000,
    azureUrl: 'https://example.com/delegation-decision-matrix',
    updatedAt: new Date(getDateOffset(-9, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80',
    title: 'Delegation Decision Matrix',
    placement: ResourcePlacement.ResourcesPage,
    clientIds: ['client-dev-1'],
    cohortIds: ['dev-group-1']
  },
  {
    id: 'learning-tool-resource-3',
    filename: 'one-on-one-agenda-templates.pdf',
    mimeType: 'application/pdf',
    type: FileObjectType.LearningTool,
    status: FileObjectStatus.Active,
    size: 640000,
    azureUrl: 'https://example.com/one-on-one-agenda-templates',
    updatedAt: new Date(getDateOffset(-12, 8)),
    thumbnailUrl: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80',
    title: 'One-on-One Agenda Templates',
    placement: ResourcePlacement.ResourcesPage,
    clientIds: ['client-dev-1'],
    // WHY: cohort-unrestricted so every persona's Learning page keeps at least one visible resource (POL-10).
    cohortIds: []
  }
];

// WHY: VideoMediaCarousel uses a direct YouTube transport, so these deterministic items make
// Workshop Library paging demonstrable without external API access in dev preview (POL-11).
export const baseLearningVideos = [
  {
    id: 'dev-video-item-1',
    snippet: {
      title: 'Leading with clarity',
      resourceId: { videoId: 'dev-workshop-video-1' },
      thumbnails: {
        maxres: {
          url: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1280&q=80'
        }
      }
    }
  },
  {
    id: 'dev-video-item-2',
    snippet: {
      title: 'Coaching conversations that move work forward',
      resourceId: { videoId: 'dev-workshop-video-2' },
      thumbnails: {
        maxres: {
          url: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1280&q=80'
        }
      }
    }
  },
  {
    id: 'dev-video-item-3',
    snippet: {
      title: 'Turning reflection into action',
      resourceId: { videoId: 'dev-workshop-video-3' },
      thumbnails: {
        maxres: {
          url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1280&q=80'
        }
      }
    }
  }
];

// --- Survey Fixtures ---

export const baseSurveys: DevSurveyFixture[] = [
  {
    id: 'dev-survey-onboarding',
    clientId: 'client-dev-1',
    company: 'Northwind Labs',
    type: 'Onboarding',
    target: 'all',
    code: 'dev-preview'
  }
];

// --- Terms & Notes ---

export const termsAndConditionsHtml = `
<div style="font-family: var(--font-family-sans); color: var(--text-primary); max-width: 1024px; text-align: left;">
  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">1. Canceled or Missed Sessions</h3>
  <ul style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); padding-left: 20px; list-style-type: disc; text-align: left;">
    <li style="margin-bottom: 8px;"><strong>1:1 Sessions</strong>: Please allow at least 48 hours before canceling a session to be rescheduled. Sessions canceled within 48 hours of the upcoming session may not be rescheduled.</li>
    <li style="margin-bottom: 8px;"><strong>Group Sessions</strong>: Group sessions cannot be canceled unless the mentor or coach is unavailable.</li>
    <li><strong>No shows</strong>: These sessions will not be rescheduled. If more than 2 no shows, the cohort will end.</li>
  </ul>
  
  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">2. Confidentiality</h3>
  <p style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">All communications with your match are confidential, but BestHuman/Locelle may reach out to ask for testimonials to be shared for marketing purposes.</p>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">3. Satisfaction</h3>
  <ul style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); padding-left: 20px; list-style-type: disc; text-align: left;">
    <li style="margin-bottom: 8px;"><strong>1:1</strong>: If you are not satisfied with your match, please let your Client Success team member know.</li>
    <li><strong>Group</strong>: If you are not satisfied with the group, we will cancel your plan and refund for all remaining sessions.</li>
  </ul>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">4. Liability</h3>
  <p style="margin-bottom: 16px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">Locelle, the mentors, coaches and/or your employer is not liable or responsible for any decisions you make as a result of participating in the program.</p>
  <p style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">As a participant, you consent to confidentiality and non-disclosure regarding sessions facilitated by Locelle and all its programs.</p>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">5. Refund or Cancelation</h3>
  <ul style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); padding-left: 20px; list-style-type: disc; text-align: left;">
    <li style="margin-bottom: 8px;">After onboarding, if you have not yet been matched or started a program, you may qualify for a refund minus a $100 admin fee.</li>
    <li style="margin-bottom: 8px;">If matched, you may qualify for a refund of $200.</li>
    <li>No refunds for sessions attended.</li>
  </ul>

  <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 12px; color: var(--text-primary); text-align: left;">6. Privacy Policy</h3>
  <p style="margin-bottom: 16px; font-size: 14px; line-height: 1.6; color: var(--text-secondary); text-align: left;">If you choose to use our Service, then you agree to the collection and use of information in relation to this policy.</p>
</div>
`;

export function defaultNotesTemplate(participantCount: number): string {
  return participantCount === 1
    ? '<h2>One-on-One Session Notes</h2><p>Capture goals, themes, and next steps here.</p>'
    : '<h2>Group Session Notes</h2><p>Capture the main discussion, themes, and follow-ups here.</p>';
}
